/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/voiceGuidesApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API functions for Voice Guides.
    Centralizes all API calls related to voice guides.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  VoiceGuidesFetchParams,
  ListVoiceGuidesResponse,
  VoiceGuide,
  LanguageOptionsResponse,
  FallbackVoiceGuideResponse,
} from '../types';

/**
 * Fetch Voice Guides with pagination and search
 */
export async function fetchVoiceGuides(
  params: VoiceGuidesFetchParams,
): Promise<ListVoiceGuidesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.status && params.status !== 'all' && { status: params.status }),
    ...(params.fallbackOnly && { fallbackOnly: 'true' }),
  });

  const url = `${buildApiUrl('/api/telephony/voice-guides')}?${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch voice guides');
  }

  return result;
}

/**
 * Fetch single Voice Guide by ID
 */
export async function fetchVoiceGuideById(
  id: string | number,
): Promise<VoiceGuide> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/voice-guides/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch voice guide');
  }

  return result.data;
}

/**
 * Fetch Voice Guides by language code
 */
export async function fetchVoiceGuidesByLanguage(
  languageCode: string,
): Promise<VoiceGuide[]> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/voice-guides/language/${languageCode}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch voice guides by language');
  }

  return result.data || [];
}

/**
 * Fetch language options for selectors
 */
export async function fetchLanguageOptions(): Promise<LanguageOptionsResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/voice-guides/language-options'),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch language options');
  }

  return result;
}

/**
 * Fetch fallback voice guide
 */
export async function fetchFallbackVoiceGuide(): Promise<FallbackVoiceGuideResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/voice-guides/fallback'),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch fallback voice guide');
  }

  return result;
}

/**
 * Generate Voice Guide Input Type
 */
export interface GenerateVoiceGuideInput {
  name: string;
  description?: string;
  language_code: string;
  dialect_code: string;
  voice_name: string;
  ai_profile_id: number | string;
  voice_guide_id?: number; // For batch mode: skip creating new record, append to existing
  start_silence_ms?: number; // Silence padding at start of audio (default: 50)
  end_silence_ms?: number;   // Silence padding at end of audio (default: 100)
  skip_existing?: boolean;   // Skip generating files that already exist
  prompts: Array<{
    path: string;
    filename: string;
    text: string;
  }>;
}

/**
 * Generate Voice Guide Response Type
 */
export interface GenerateVoiceGuideResponse {
  success: boolean;
  data: {
    success: boolean;
    voice_guide_id?: number;
    generated_count: number;
    skipped_count: number;
    failed_count: number;
    output_path: string;
    errors?: string[];
  };
  message: string;
}

/**
 * Generate Voice Guide using TTS
 */
export async function generateVoiceGuide(
  input: GenerateVoiceGuideInput,
  signal?: AbortSignal,
): Promise<GenerateVoiceGuideResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/voice-guides/generate'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
      signal,
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update Voice Guide settings (TTS Profile, description, silences)
 * without regenerating audio files.
 */
export async function updateVoiceGuideSettings(
  id: number | string,
  data: {
    description?: string | null;
    ai_profile_id?: string | number | null; // Sqids string or integer; backend decodes
    start_silence_ms?: number;
    end_silence_ms?: number;
  },
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/voice-guides/${id}`),
    {
      method: 'PATCH',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}
/**
 * Delete Voice Guide by ID
 * Only works for custom (non-fallback) voice guides
 */
export async function deleteVoiceGuide(
  id: number | string,
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/voice-guides/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Voice Guide Audio File entry
 */
export interface VoiceGuideAudioFile {
  relativePath: string;
  size: number;
  modifiedAt: string;
}

/**
 * List audio files for a Voice Guide
 */
export async function fetchVoiceGuideFiles(
  id: number | string,
  search?: string,
): Promise<{ success: boolean; data: VoiceGuideAudioFile[]; total: number; dir: string }> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  const url = `${buildApiUrl(`/api/telephony/voice-guides/${id}/files`)}${params.toString() ? '?' + params.toString() : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error((errorBody as { message?: string }).message || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Build a URL to stream a WAV audio file from the server.
 * Uses the authenticated API base so the request carries the Authorization header.
 */
export function getVoiceGuideStreamUrl(absoluteOrRelativePath: string): string {
  return `${buildApiUrl('/api/telephony/voice-guides/stream')}?path=${encodeURIComponent(absoluteOrRelativePath)}`;
}
